import { forwardRef } from "react";
import {
  Textarea as ChakraTextarea,
  TextareaProps as ChakraTextareaProps,
} from "@chakra-ui/react";
import type { TextareaSharedProps } from "./Textarea.props";

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (props, ref) => {
    // Extract props explicitly so we can control prop names in one place
    const {
      maxLength,
      maxHeight,
      fontSize = "md",
      isInvalid,
      invalid,
      isDisabled,
      disabled,
      isRequired,
      required,
      isReadOnly,
      readOnly,
      ...rest
    } = props;

    // Map v1/v2 props to v3 props for backward compatibility
    const invalidValue = invalid !== undefined ? invalid : isInvalid;
    const disabledValue = disabled !== undefined ? disabled : isDisabled;
    const requiredValue = required !== undefined ? required : isRequired;
    const readOnlyValue = readOnly !== undefined ? readOnly : isReadOnly;

    return (
      <ChakraTextarea
        ref={ref}
        maxLength={maxLength}
        maxHeight={maxHeight ?? "200px"}
        aria-invalid={invalidValue}
        disabled={disabledValue}
        required={requiredValue}
        readOnly={readOnlyValue}
        border="1px solid"
        borderColor="gray.400"
        fontSize={fontSize}
        padding="12px 16px"
        _placeholder={{ color: "gray.600", opacity: 1 }}
        _focus={{
          borderColor: "primary.solid",
          boxShadow: "0 0 0 1px var(--chakra-colors-primary-solid)",
        }}
        _invalid={{
          borderColor: "error.solid",
          boxShadow: "0 0 0 1px var(--chakra-colors-error-solid)",
        }}
        {...rest}
      />
    );
  }
);

Textarea.displayName = "Textarea";

export default Textarea;

export type TextareaProps = TextareaSharedProps &
  Omit<ChakraTextareaProps, "invalid"> & {
    // Add any web specific props here.
  };
